import React, { useRef, useState } from 'react';
import { X, Send, Loader2, Zap, Sparkles, Bot, Film, Layers, Upload, Wand2, ImagePlus, Trash2, Crown, MousePointerClick, ExternalLink } from 'lucide-react';
import { ContentTypeSelector } from './ContentTypeSelector';
import { ReelsMediaSelector } from './ReelsMediaSelector';
import { StoryPartEditor } from './StoryPartEditor';
import { StoryPhonePreview } from './StoryPhonePreview';
import { PlatformSelector } from './PlatformSelector';
import { ScheduleSelector } from './ScheduleSelector';
import { WebhookConfig } from './WebhookConfig';
import { AIContentAssistant } from './AIContentAssistant';
import { useReelsStoryTaskForm } from '../../hooks/useReelsStoryTaskForm';
import type { ReelsStoryTaskFormProps } from './types';

export const ReelsStoryTaskForm: React.FC<ReelsStoryTaskFormProps> = ({
  onClose,
  onSubmit,
  editingTask,
  defaultContentType,
}) => {
  const form = useReelsStoryTaskForm({ onClose, onSubmit, editingTask, defaultContentType });
  const refImageInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [activePreviewSlide, setActivePreviewSlide] = useState(0);

  const isReel = form.contentType === 'reel';
  const isStory = form.contentType === 'story';
  const accentColor = isReel ? 'purple' : 'pink';
  const accentGradient = isReel
    ? 'from-purple-600 to-violet-600'
    : 'from-pink-500 to-orange-500';

  const isUploadMode = isReel ? form.reelMode === 'upload' : form.storyMode === 'upload';
  const isAiMode = isReel ? form.reelMode === 'ai_generate' : form.storyMode === 'ai_generate';
  const isStoryUploadMode = isStory && form.storyMode === 'upload';
  const isStoryAiMode = isStory && form.storyMode === 'ai_generate';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className={`bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] overflow-hidden flex flex-col transition-all duration-300 ${isStoryUploadMode && form.storyParts.length > 0 ? 'max-w-5xl' : 'max-w-2xl'}`}>
        {/* Header */}
        <div className={`bg-gradient-to-r ${accentGradient} px-6 py-4 text-white flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            {isReel ? <Film className="w-6 h-6" /> : <Layers className="w-6 h-6" />}
            <div>
              <h2 className="text-lg font-bold">
                {editingTask ? 'Edytuj' : 'Zaplanuj'} {isReel ? 'Reel' : 'Story'}
              </h2>
              <p className="text-sm text-white/80">
                {isReel ? 'Krótkie wideo w formacie pionowym' : 'Historia — jedno- lub wieloelementowa'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Content Type Selector */}
          <ContentTypeSelector
            selectedContentType={form.contentType}
            onContentTypeSelect={form.handleContentTypeSelectForReelsStory}
          />

          {/* Platform & Account Selector */}
          <PlatformSelector
            selectedPlatform={form.selectedPlatform}
            onPlatformSelect={form.handlePlatformSelectForReelsStory}
            connectedAccounts={form.connectedAccounts as any}
            selectedAccount={form.selectedAccount}
            onAccountSelect={form.handleAccountSelectForReelsStory}
          />

          {/* === REEL MODE SELECTOR === */}
          {isReel && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Film className="w-4 h-4 text-purple-600" />
                Źródło wideo
              </label>
              <div className="grid grid-cols-2 gap-3">
                {/* Upload Mode */}
                <button
                  type="button"
                  onClick={() => form.setReelMode('upload')}
                  className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all text-center ${
                    isUploadMode
                      ? 'border-purple-500 bg-purple-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-purple-300 hover:bg-purple-50/30'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isUploadMode ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-500'
                  }`}>
                    <Upload className="w-5 h-5" />
                  </div>
                  <span className={`text-sm font-semibold ${isUploadMode ? 'text-purple-700' : 'text-gray-600'}`}>
                    Wgraj swój film
                  </span>
                  <span className="text-[11px] text-gray-400 leading-tight">
                    Masz gotowy film? Wgraj i opublikuj bezpośrednio
                  </span>
                  {isUploadMode && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
                
                {/* AI Generate Mode */}
                <button
                  type="button"
                  onClick={() => form.setReelMode('ai_generate')}
                  className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all text-center ${
                    isAiMode
                      ? 'border-violet-500 bg-violet-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-violet-300 hover:bg-violet-50/30'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isAiMode ? 'bg-gradient-to-br from-violet-500 to-purple-600 text-white' : 'bg-gray-100 text-gray-500'
                  }`}>
                    <Wand2 className="w-5 h-5" />
                  </div>
                  <span className={`text-sm font-semibold ${isAiMode ? 'text-violet-700' : 'text-gray-600'}`}>
                    Wygeneruj film AI
                  </span>
                  <span className="text-[11px] text-gray-400 leading-tight">
                    AI stworzy wideo na podstawie Twoich materiałów
                  </span>
                  {isAiMode && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-violet-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* === STORY MODE SELECTOR === */}
          {isStory && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Layers className="w-4 h-4 text-pink-600" />
                Źródło materiałów
              </label>
              <div className="grid grid-cols-2 gap-3">
                {/* Upload Mode */}
                <button
                  type="button"
                  onClick={() => form.setStoryMode('upload')}
                  className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all text-center ${
                    isStoryUploadMode
                      ? 'border-pink-500 bg-pink-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-pink-300 hover:bg-pink-50/30'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isStoryUploadMode ? 'bg-gradient-to-br from-pink-500 to-orange-500 text-white' : 'bg-gray-100 text-gray-500'
                  }`}>
                    <Upload className="w-5 h-5" />
                  </div>
                  <span className={`text-sm font-semibold ${isStoryUploadMode ? 'text-pink-700' : 'text-gray-600'}`}>
                    Wgraj materiały
                  </span>
                  <span className="text-[11px] text-gray-400 leading-tight">
                    Zdjęcia i/lub wideo — stwórz slajdy ręcznie
                  </span>
                  {isStoryUploadMode && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-pink-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>

                {/* AI Generate Mode */}
                <button
                  type="button"
                  onClick={() => form.setStoryMode('ai_generate')}
                  className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all text-center ${
                    isStoryAiMode
                      ? 'border-orange-500 bg-orange-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-orange-300 hover:bg-orange-50/30'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isStoryAiMode ? 'bg-gradient-to-br from-orange-500 to-pink-600 text-white' : 'bg-gray-100 text-gray-500'
                  }`}>
                    <Wand2 className="w-5 h-5" />
                  </div>
                  <span className={`text-sm font-semibold ${isStoryAiMode ? 'text-orange-700' : 'text-gray-600'}`}>
                    Wygeneruj film AI
                  </span>
                  <span className="text-[11px] text-gray-400 leading-tight">
                    AI stworzy wideo Story na podstawie Twoich materiałów
                  </span>
                  {isStoryAiMode && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Description / Caption */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              {isReel ? (
                <>
                  <Film className="w-4 h-4 text-purple-600" />
                  <span>{isAiMode ? 'Instrukcje dla AI / Opis Reel' : 'Opis Reel'}</span>
                </>
              ) : (
                <>
                  <Layers className="w-4 h-4 text-pink-600" />
                  <span>Treść historii</span>
                  <span className="text-xs text-gray-400">(opcjonalnie — dla auto-podziału)</span>
                </>
              )}
            </label>
            <textarea
              value={form.content}
              onChange={(e) => form.setContent(e.target.value)}
              className={`w-full min-h-[80px] p-3 border rounded-xl focus:ring-2 focus:ring-${accentColor}-400 focus:border-${accentColor}-400 resize-y text-sm`}
              placeholder={
                isReel
                  ? (isAiMode
                    ? 'Opisz jaki film ma stworzyć AI: temat, styl, przekaz, długość...'
                    : 'Opis do Reela — wyświetli się pod wideo...')
                  : isStoryAiMode
                  ? 'Opisz jakie Story ma stworzyć AI: temat, styl, liczba slajdów, przekaz...'
                  : 'Wpisz treść i użyj „Auto-podział" aby rozdzielić na slajdy...'
              }
            />
            {form.content.length > 0 && (
              <div className="text-xs text-gray-400 mt-1 text-right">
                {form.content.length} znaków
              </div>
            )}
          </div>

          {/* AI Content Assistant Toggle */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={form.handleAIAssistantToggleForReelsStory}
              className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                form.showAIAssistant
                  ? 'bg-violet-50 border-violet-300 text-violet-700'
                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Bot className="w-3.5 h-3.5" />
              AI Asystent
            </button>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isAiGenerated}
                onChange={form.handleAiGeneratedToggleForReelsStory}
                className={`rounded border-gray-300 text-${accentColor}-600 focus:ring-${accentColor}-500`}
              />
              <span className="text-xs text-gray-600 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                Użyto AI do generacji treści
              </span>
            </label>
          </div>

          {/* AI Assistant Panel */}
          {form.showAIAssistant && (
            <AIContentAssistant
              onContentGenerated={form.handleAIContentGeneratedForReelsStory}
              onClose={form.handleAIAssistantToggleForReelsStory}
              contentType={form.contentType}
              existingSlides={
                isStory && isStoryUploadMode && form.storyParts.length > 0
                  ? form.storyParts.map((p, i) => ({
                      index: i,
                      hasMedia: !!p.mediaUrl,
                      mediaType: p.mediaType,
                    }))
                  : undefined
              }
            />
          )}

          {/* ═══ UPLOAD MODE: Video + Cover ═══ */}
          {isReel && isUploadMode && (
            <ReelsMediaSelector
              videoUrl={form.videoUrl}
              coverImageUrl={form.coverImageUrl}
              isUploading={form.isUploading}
              onVideoChange={form.handleReelsVideoChange}
              onCoverImageChange={form.handleReelsCoverImageChange}
              onVideoRemove={form.handleReelsVideoRemove}
              onCoverImageRemove={form.handleReelsCoverImageRemove}
            />
          )}

          {/* ═══ REELS CTA (both upload and AI modes) ═══ */}
          {isReel && (
            <div className="rounded-xl border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <MousePointerClick className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-semibold text-emerald-800">CTA — Call-to-Action</span>
                <span className="text-[10px] text-emerald-500 bg-emerald-100 px-2 py-0.5 rounded-full font-medium">opcjonalnie</span>
              </div>
              <p className="text-[11px] text-emerald-600/80 -mt-1">
                Dodaj link i przycisk CTA — zostaną dołączone do opisu Reela
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div>
                  <label className="flex items-center gap-1.5 text-[10px] font-semibold text-emerald-700 uppercase tracking-wide mb-1">
                    <ExternalLink className="w-3 h-3" />
                    Link URL
                  </label>
                  <input
                    type="url"
                    value={form.reelCtaUrl}
                    onChange={(e) => form.setReelCtaUrl(e.target.value)}
                    placeholder="https://example.com/promo"
                    className="w-full px-3 py-2 text-xs border border-emerald-200 rounded-lg focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 bg-white placeholder:text-emerald-300 transition-all"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-1.5 text-[10px] font-semibold text-emerald-700 uppercase tracking-wide mb-1">
                    <MousePointerClick className="w-3 h-3" />
                    Tekst przycisku
                  </label>
                  <input
                    type="text"
                    value={form.reelCtaButtonText}
                    onChange={(e) => form.setReelCtaButtonText(e.target.value)}
                    placeholder='Np. "Kup teraz" lub "Sprawdź →"'
                    className="w-full px-3 py-2 text-xs border border-emerald-200 rounded-lg focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 bg-white placeholder:text-emerald-300 transition-all"
                  />
                </div>
              </div>
              {/* Preview of CTA in content */}
              {(form.reelCtaUrl || form.reelCtaButtonText) && (
                <div className="mt-1 px-3 py-2 bg-white/80 rounded-lg border border-emerald-100 text-[11px] text-emerald-700">
                  <span className="font-medium">Podgląd w opisie:</span>
                  <span className="ml-1 opacity-70">
                    {form.reelCtaButtonText && `🔗 ${form.reelCtaButtonText}`}
                    {form.reelCtaUrl && ` · ${form.reelCtaUrl}`}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ═══ AI GENERATE MODE: Reference Images + Logo ═══ */}
          {isReel && isAiMode && (
            <div className="space-y-4">
              {/* Reference Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <ImagePlus className="w-4 h-4 text-violet-600" />
                  <span>Zdjęcia referencyjne</span>
                  <span className="text-xs text-gray-400">(max 3 — zostaną użyte w wideo)</span>
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {form.referenceImages.map((url, idx) => (
                    <div key={idx} className="relative group rounded-xl overflow-hidden border-2 border-violet-200 aspect-square bg-gray-50">
                      <img src={url} alt={`Ref ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => form.handleReferenceImageRemove(idx)}
                        disabled={form.isUploading}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                      <div className="absolute bottom-1 left-1 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                        #{idx + 1}
                      </div>
                    </div>
                  ))}
                  {form.referenceImages.length < 3 && (
                    <button
                      type="button"
                      onClick={() => refImageInputRef.current?.click()}
                      disabled={form.isUploading}
                      className="aspect-square rounded-xl border-2 border-dashed border-violet-300 bg-violet-50/50 flex flex-col items-center justify-center gap-1 hover:bg-violet-50 hover:border-violet-400 transition-colors disabled:opacity-50"
                    >
                      {form.isUploading ? (
                        <Loader2 className="w-5 h-5 animate-spin text-violet-400" />
                      ) : (
                        <>
                          <ImagePlus className="w-5 h-5 text-violet-400" />
                          <span className="text-[10px] text-violet-500 font-medium">Dodaj zdjęcie</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
                <input
                  ref={refImageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={form.handleReferenceImageUpload}
                  className="hidden"
                />
              </div>

              {/* Logo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Crown className="w-4 h-4 text-amber-500" />
                  <span>Logotyp</span>
                  <span className="text-xs text-gray-400">(opcjonalnie — zostanie nałożony na wideo)</span>
                </label>
                {form.logoUrl ? (
                  <div className="relative inline-block group">
                    <div className="w-20 h-20 rounded-xl overflow-hidden border-2 border-amber-200 bg-white shadow-sm">
                      <img src={form.logoUrl} alt="Logo" className="w-full h-full object-contain p-1" />
                    </div>
                    <button
                      type="button"
                      onClick={form.handleLogoRemove}
                      disabled={form.isUploading}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-red-600"
                    >
                      <Trash2 className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={form.isUploading}
                    className="w-20 h-20 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/50 flex flex-col items-center justify-center gap-1 hover:bg-amber-50 hover:border-amber-400 transition-colors disabled:opacity-50"
                  >
                    {form.isUploading ? (
                      <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
                    ) : (
                      <>
                        <Crown className="w-5 h-5 text-amber-400" />
                        <span className="text-[9px] text-amber-500 font-medium">Logo</span>
                      </>
                    )}
                  </button>
                )}
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={form.handleLogoUpload}
                  className="hidden"
                />
              </div>

              {/* Cover Image (optional in AI mode too) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <ImagePlus className="w-4 h-4 text-gray-500" />
                  <span>Okładka</span>
                  <span className="text-xs text-gray-400">(opcjonalnie — możesz też wgrać ręcznie)</span>
                </label>
                <ReelsMediaSelector
                  videoUrl={null}
                  coverImageUrl={form.coverImageUrl}
                  isUploading={form.isUploading}
                  onVideoChange={() => {}}
                  onCoverImageChange={form.handleReelsCoverImageChange}
                  onVideoRemove={() => {}}
                  onCoverImageRemove={form.handleReelsCoverImageRemove}
                  coverOnly
                />
              </div>
            </div>
          )}

          {/* ═══ STORY AI GENERATE MODE: Reference Images + Logo ═══ */}
          {isStoryAiMode && (
            <div className="space-y-4">
              {/* Reference Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <ImagePlus className="w-4 h-4 text-orange-600" />
                  <span>Zdjęcia referencyjne</span>
                  <span className="text-xs text-gray-400">(max 3 — zostaną użyte do stworzenia Story)</span>
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {form.referenceImages.map((url, idx) => (
                    <div key={idx} className="relative group rounded-xl overflow-hidden border-2 border-orange-200 aspect-square bg-gray-50">
                      <img src={url} alt={`Ref ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => form.handleReferenceImageRemove(idx)}
                        disabled={form.isUploading}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                      <div className="absolute bottom-1 left-1 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                        #{idx + 1}
                      </div>
                    </div>
                  ))}
                  {form.referenceImages.length < 3 && (
                    <button
                      type="button"
                      onClick={() => refImageInputRef.current?.click()}
                      disabled={form.isUploading}
                      className="aspect-square rounded-xl border-2 border-dashed border-orange-300 bg-orange-50/50 flex flex-col items-center justify-center gap-1 hover:bg-orange-50 hover:border-orange-400 transition-colors disabled:opacity-50"
                    >
                      {form.isUploading ? (
                        <Loader2 className="w-5 h-5 animate-spin text-orange-400" />
                      ) : (
                        <>
                          <ImagePlus className="w-5 h-5 text-orange-400" />
                          <span className="text-[10px] text-orange-500 font-medium">Dodaj zdjęcie</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
                <input
                  ref={refImageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={form.handleReferenceImageUpload}
                  className="hidden"
                />
              </div>

              {/* Logo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Crown className="w-4 h-4 text-amber-500" />
                  <span>Logotyp</span>
                  <span className="text-xs text-gray-400">(opcjonalnie — zostanie nałożony na wideo)</span>
                </label>
                {form.logoUrl ? (
                  <div className="relative inline-block group">
                    <div className="w-20 h-20 rounded-xl overflow-hidden border-2 border-amber-200 bg-white shadow-sm">
                      <img src={form.logoUrl} alt="Logo" className="w-full h-full object-contain p-1" />
                    </div>
                    <button
                      type="button"
                      onClick={form.handleLogoRemove}
                      disabled={form.isUploading}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-red-600"
                    >
                      <Trash2 className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={form.isUploading}
                    className="w-20 h-20 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/50 flex flex-col items-center justify-center gap-1 hover:bg-amber-50 hover:border-amber-400 transition-colors disabled:opacity-50"
                  >
                    {form.isUploading ? (
                      <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
                    ) : (
                      <>
                        <Crown className="w-5 h-5 text-amber-400" />
                        <span className="text-[9px] text-amber-500 font-medium">Logo</span>
                      </>
                    )}
                  </button>
                )}
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={form.handleLogoUpload}
                  className="hidden"
                />
              </div>
            </div>
          )}

          {/* Story Parts Editor + Phone Preview (shown when contentType === 'story' AND upload mode) */}
          {isStory && isStoryUploadMode && (
            <div className={`${form.storyParts.length > 0 ? 'flex flex-col lg:flex-row gap-6' : ''}`}>
              {/* Editor column */}
              <div className={`${form.storyParts.length > 0 ? 'flex-1 min-w-0' : ''}`}>
                <StoryPartEditor
                  parts={form.storyParts}
                  onAddPart={form.handleAddStoryPart}
                  onRemovePart={form.handleRemoveStoryPart}
                  onUpdatePart={form.handleUpdateStoryPart}
                  onMovePart={form.handleMoveStoryPart}
                  onAutoSplit={form.handleAutoSplitContentForStory}
                  onPartMediaChange={form.handleStoryPartMediaChange}
                  onVideoAutoSplit={form.handleVideoAutoSplit}
                  isUploading={form.isUploading}
                  isVideoSplitting={form.isVideoSplitting}
                  autoSplitAvailable={form.content.trim().length > 0}
                  videoSplitInfo={form.videoSplitInfo}
                  onSetSegmentDuration={form.handleSetVideoSegmentDuration}
                  onConfirmVideoSplit={form.handleConfirmVideoSplit}
                  onCancelVideoSplit={form.handleCancelVideoSplit}
                />
              </div>
              {/* Phone preview column — sticky */}
              {form.storyParts.length > 0 && (
                <div className="lg:w-[260px] flex-shrink-0 lg:sticky lg:top-0 lg:self-start">
                  <StoryPhonePreview
                    parts={form.storyParts}
                    activeSlideIndex={activePreviewSlide}
                    onSlideChange={setActivePreviewSlide}
                  />
                </div>
              )}
            </div>
          )}

          {/* Schedule */}
          <ScheduleSelector
            selectedDate={form.selectedDate}
            selectedTime={form.selectedTime}
            selectedTimezone={form.selectedTimezone}
            cycle={form.cycle}
            selectedDays={form.selectedDays}
            minTime={form.minTime}
            userDefaultTimezone={form.userDefaultTimezone}
            onDateChange={form.handleDateChangeForReelsStory}
            onTimeChange={form.handleTimeChangeForReelsStory}
            onTimezoneChange={form.handleTimezoneChangeForReelsStory}
            onCycleChange={form.handleCycleChangeForReelsStory}
            onDaysChange={form.handleDaysChangeForReelsStory}
          />

          {/* Webhook Config — only shown in AI generate mode or for Stories in upload mode */}
          {((!isReel && isStoryUploadMode) || isAiMode) && (
            <WebhookConfig
              webhookUrl={form.customWebhook}
              showInfo={form.showWebhookInfo}
              onWebhookChange={form.handleWebhookChangeForReelsStory}
              onToggleInfo={form.handleWebhookInfoToggleForReelsStory}
            />
          )}
        </div>

        {/* Footer Actions */}
        <div className={`border-t bg-gray-50 px-6 py-4 flex items-center justify-between`}>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Anuluj
          </button>
          <div className="flex gap-3">
            {/* Test button — only for webhook-based modes */}
            {(isAiMode || isStoryUploadMode) && (
              <button
                onClick={form.handleTestReelsStoryWebhook}
                disabled={form.isLoading || form.isTesting || form.isUploading}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-2 border-${accentColor}-500 text-${accentColor}-700 rounded-xl hover:bg-${accentColor}-50 transition-colors disabled:opacity-50`}
              >
                {form.isTesting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Zap className="w-4 h-4" />
                )}
                {form.isTesting ? 'Testing...' : 'Testuj'}
              </button>
            )}
            <button
              onClick={form.handleSubmitReelsStoryTask}
              disabled={form.isLoading || form.isTesting || form.isUploading}
              className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r ${accentGradient} rounded-xl hover:shadow-lg transition-all disabled:opacity-50`}
            >
              {form.isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {editingTask ? 'Zapisz zmiany' : `Zaplanuj ${isReel ? 'Reel' : 'Story'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
